import {
    DrivewealthAccountMigrationState,
    AccountMigrationPeriodStatus,
    AccountMigrationPeriodStatusResponse,
    FetchState,
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import * as drivewealthService from "@/shared/api/drivewealthService";
import _ from "lodash";
import moment from "moment";
import { PartnerAccountsMigrationModel } from "@/admin/types";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: DrivewealthAccountMigrationState = {
    fetchState: FetchState.Uninitialized,
    partnerAccountMigration: {
        id: 0,
        partnerId: 0,
        dateCreated: new Date(),
        dateEnd: moment().add(1, "days").toDate(),
        dateStart: new Date(),
        dateLastModified: new Date()
    },
    accountMigrationPeriodStatus: AccountMigrationPeriodStatus.None
};

export const useDrivewealthAccountMigrationStore = defineStore("drivewealthAccountMigration", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        partnerAccountsMigrationLoaded: state => state.fetchState === FetchState.Loaded,
        currentPartnerAccountMigration: state => state.partnerAccountMigration,
        hasAccountMigration: state => state.accountMigrationPeriodStatus !== AccountMigrationPeriodStatus.None,
        accountMigrationNotStarted: state => state.accountMigrationPeriodStatus === AccountMigrationPeriodStatus.NotStarted,
        accountMigrationActive: state => state.accountMigrationPeriodStatus === AccountMigrationPeriodStatus.Active,
        accountMigrationExpired: state => state.accountMigrationPeriodStatus === AccountMigrationPeriodStatus.Expired,
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setPartnerAccountMigration (migrationRequest: PartnerAccountsMigrationModel) {
            if (migrationRequest === null) {
                this.partnerAccountMigration = initialState.partnerAccountMigration;
            } else {
                this.partnerAccountMigration = migrationRequest;
            }
        },
        loadPartnerAccountsMigration (partnerId: number) {
            this.setFetchState(FetchState.Loading);
            return drivewealthService.getPartnerAccountsMigration(partnerId)
                .then(result => {
                    this.setPartnerAccountMigration(result.data);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    this.setFetchState(FetchState.Loaded);
                });
        },
        updatePartnerAccountsMigration (accountsMigrationRequest: PartnerAccountsMigrationModel) {
            this.setFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();
            return drivewealthService.updatePartnerAccountsMigration(accountsMigrationRequest.partnerId,
                    accountsMigrationRequest)
                .then(result => {
                    notifierStore.setSuccess("Partner accounts migration saved.");
                    this.setPartnerAccountMigration(result.data);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    this.setFetchState(FetchState.Loaded);
                });
        },
        createPartnerAccountsMigration (accountsMigrationRequest: PartnerAccountsMigrationModel) {
            this.setFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();

            return drivewealthService.createPartnerAccountsMigration(accountsMigrationRequest.partnerId,
                    accountsMigrationRequest)
                .then(result => {
                    notifierStore.setSuccess("Partner accounts migration saved.");
                    this.setPartnerAccountMigration(result.data);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    this.setFetchState(FetchState.Loaded);
                });
        },
        setAccountMigrationPeriodStatus (accountMigrationPeriodStatus: AccountMigrationPeriodStatus) {
            this.accountMigrationPeriodStatus = accountMigrationPeriodStatus;
        },
        getAccountMigrationPeriodStatus () {
            this.setFetchState(FetchState.Loading);

            if (this.fetchState === FetchState.Loaded) {
                return Promise.resolve();
            }

            return drivewealthService.getAccountMigrationPeriodStatus()
                .then(result => {
                    this.setAccountMigrationPeriodStatus(result.data.accountMigrationPeriodStatus);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    if (err && err.response && err.response.status !== 404) {
                        return storeHelper.handleError({ error: err, rethrow: true });
                    }
                    this.setFetchState(FetchState.Loaded);
                });
        },
    }
});